import { useMemo } from 'react'

import { SettingsPath } from 'routes/paths'

import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { isZeusEnvironment } from 'utils/environment'

export enum SettingTab {
  Overview = 'Overview',
  Pricing = 'Pricing',
  Accounting = 'Accounting',
  Tax = 'Tax',
  Team = 'Team',
  SpamTokens = 'Spam tokens',
  StableCoins = 'Stable coins',
  OrgControls = 'Org Controls',
  Requests = 'Requests',
  Categories = 'Categories',
  Entities = 'Entities',
  Organization = 'Organization',
  ImposterTokenAllowlist = 'Imposter Allowlist',
  IngestionJobs = 'Ingestion Jobs',
}

export type SettingTabInfo = {
  title: string
  route: string
}

/**
 * Hook that returns setting tab metadata (title and route) for use in command palette and navigation.
 * This is a lightweight version that doesn't import the actual tab components.
 */
export const useSettingTabs = () => {
  const {
    shouldShowPricingSettings,
    isStableCoinsEnabled,
    showCategoriesSettings,
    isMultiBookEnabled,
  } = useFeatureFlags()
  const { hasPermission } = useUser()

  return useMemo(
    () =>
      ({
        [SettingTab.Overview]: {
          title: SettingTab.Overview,
          route: SettingsPath.Overview,
        },
        ...(shouldShowPricingSettings
          ? {
              [SettingTab.Pricing]: {
                title: SettingTab.Pricing,
                route: SettingsPath.Pricing,
              },
            }
          : {}),
        ...(isZeusEnvironment() || !hasPermission(Permission.SettingConfigRead)
          ? {}
          : {
              [SettingTab.Accounting]: {
                title: SettingTab.Accounting,
                route: SettingsPath.Accounting,
              },
            }),
        ...(isMultiBookEnabled && hasPermission(Permission.SettingConfigRead)
          ? {
              [SettingTab.Tax]: {
                title: SettingTab.Tax,
                route: SettingsPath.Tax,
              },
            }
          : {}),
        [SettingTab.Team]: {
          title: SettingTab.Team,
          route: SettingsPath.Team,
        },
        ...(isZeusEnvironment() || !hasPermission(Permission.SettingSpamTokenRead)
          ? {}
          : {
              [SettingTab.SpamTokens]: {
                title: SettingTab.SpamTokens,
                route: SettingsPath.SpamTokens,
              },
            }),
        ...(isZeusEnvironment()
          ? {}
          : {
              [SettingTab.OrgControls]: {
                title: SettingTab.OrgControls,
                route: SettingsPath.OrgControls,
              },
            }),
        [SettingTab.Requests]: {
          title: SettingTab.Requests,
          route: SettingsPath.Requests,
        },
        [SettingTab.Entities]: {
          title: SettingTab.Entities,
          route: SettingsPath.LegalEntity,
        },
        [SettingTab.Organization]: {
          title: SettingTab.Organization,
          route: SettingsPath.OrgMetadata,
        },
        ...(isZeusEnvironment()
          ? {}
          : {
              [SettingTab.ImposterTokenAllowlist]: {
                title: SettingTab.ImposterTokenAllowlist,
                route: SettingsPath.ImposterAllowList,
              },
              [SettingTab.IngestionJobs]: {
                title: SettingTab.IngestionJobs,
                route: SettingsPath.IngestionJobs,
              },
            }),
        ...(isStableCoinsEnabled
          ? {
              [SettingTab.StableCoins]: {
                title: SettingTab.StableCoins,
                route: SettingsPath.StableCoins,
              },
            }
          : {}),
        ...(showCategoriesSettings && hasPermission(Permission.SettingCategoryRead)
          ? {
              [SettingTab.Categories]: {
                title: SettingTab.Categories,
                route: SettingsPath.Categories,
              },
            }
          : {}),
      }) as const,
    [
      shouldShowPricingSettings,
      isStableCoinsEnabled,
      showCategoriesSettings,
      isMultiBookEnabled,
      hasPermission,
    ]
  )
}
